import * as React from 'react'

import { AnimatePresence } from 'motion/react'
import * as m from 'motion/react-m'

import { Button, ExternalLinkIcon, SimpleSpinner } from '@buffer-mono/popcorn'

import { LoadMoreContextButton } from './LoadMoreContextButton'
import { useContextMentions } from './useContextMentions'
import {
  MentionContextProvider,
  useMentionData,
} from '../context/MentionContext'
import type { MentionData } from '../context/MentionContext'
import type { MentionedChannel } from '../../util/getMentionedChannelTextParts'
import { MentionContextItem } from '../MentionCard'

import styles from './MentionsContextSection.module.css'

import type { JSX } from 'react'

const EASE_OUT_QUAD = [0.25, 0.46, 0.45, 0.94] as const
const EASE_IN_QUAD = [0.55, 0.085, 0.68, 0.53] as const

const CONTEXT_MENTIONS_ANIMATION = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: 'auto',
    opacity: 1,
    transition: {
      height: { duration: 0.2, ease: EASE_OUT_QUAD },
      opacity: { duration: 0.2, ease: EASE_OUT_QUAD },
    },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: {
      height: { duration: 0.2, ease: EASE_IN_QUAD },
      opacity: { duration: 0.2, ease: EASE_IN_QUAD },
    },
  },
} as const

const MENTION_ITEM_ANIMATION = {
  initial: { height: 0, opacity: 0 },
  animate: { height: 'auto', opacity: 1 },
  exit: { height: 0, opacity: 0 },
  transition: { duration: 0.2, ease: EASE_OUT_QUAD },
} as const

const BUTTON_ANIMATION = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: { duration: 0.2, ease: EASE_OUT_QUAD },
  },
  exit: {
    opacity: 0,
    transition: { duration: 0.15, ease: EASE_IN_QUAD },
  },
} as const

/**
 * These controls live inside `MentionWrapper` (role="button"), which owns
 * an `onKeyDown` that toggles the card. Stop Enter/Space from bubbling so
 * activating a control doesn't also toggle the surrounding card. Mirrors
 * `MentionContextLink` in `MentionCard`.
 */
const stopCardKeyboardActivation = (
  e: React.KeyboardEvent<HTMLElement>,
): void => {
  if (e.key === 'Enter' || e.key === ' ') {
    e.stopPropagation()
  }
}

const ExpandContextButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void
    loading: boolean
    showSpinner: boolean
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, loading, showSpinner, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant="tertiary"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onKeyDown={stopCardKeyboardActivation}
      className={styles.expandContextButton}
      size="small"
      disabled={loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      Show more replies
      {showSpinner && (
        <span
          data-testid="loading-spinner"
          className={styles.spinnerWrapper}
          aria-hidden="true"
        >
          <SimpleSpinner size="small" />
        </span>
      )}
    </Button>
  )
})

ExpandContextButton.displayName = 'ExpandContextButton'

const ViewFullThreadLink = React.forwardRef<
  HTMLAnchorElement,
  {
    href: string
  }
>(({ href }, ref) => {
  return (
    <Button
      ref={ref}
      as="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="tertiary"
      size="small"
      aria-label="View full thread in new tab"
      className={styles.expandContextButton}
      onClick={(e) => {
        e.stopPropagation()
      }}
      onKeyDown={stopCardKeyboardActivation}
    >
      Show more replies
      <ExternalLinkIcon />
    </Button>
  )
})

ViewFullThreadLink.displayName = 'ViewFullThreadLink'

const CollapseContextButton = React.forwardRef<
  HTMLButtonElement,
  {
    onClick: () => void
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ onClick, ...rest }, ref) => {
  return (
    <Button
      ref={ref}
      variant="tertiary"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onKeyDown={stopCardKeyboardActivation}
      className={styles.expandContextButton}
      size="small"
      {...rest}
    >
      Hide replies
    </Button>
  )
})

CollapseContextButton.displayName = 'CollapseContextButton'

type ContextButtonsProps = {
  showContext: boolean
  isLoading: boolean
  showSpinner: boolean
  externalLink?: string | null
  panelId: string
  onExpand: () => void
  onCollapse: () => void
}

const ContextButtons = ({
  showContext,
  isLoading,
  showSpinner,
  externalLink,
  panelId,
  onExpand,
  onCollapse,
}: ContextButtonsProps): JSX.Element => {
  return (
    <AnimatePresence mode="popLayout">
      {!showContext ? (
        <m.div key="expand-button" {...BUTTON_ANIMATION}>
          <ExpandContextButton
            aria-expanded={false}
            aria-controls={panelId}
            onClick={onExpand}
            loading={isLoading}
            showSpinner={showSpinner}
          />
        </m.div>
      ) : (
        <m.div
          key="expanded-buttons"
          {...BUTTON_ANIMATION}
          className={styles.expandedButtonGroup}
        >
          {externalLink && (
            <>
              <ViewFullThreadLink href={externalLink} />
              <div className={styles.verticalLine} />
            </>
          )}
          <CollapseContextButton
            aria-expanded={true}
            aria-controls={panelId}
            onClick={onCollapse}
          />
        </m.div>
      )}
    </AnimatePresence>
  )
}

type ContextMentionsListProps = {
  mentions: MentionData[]
  hasMore: boolean
  totalCount: number
  visibleCount: number
  panelId: string
  onLoadMore: () => void
  channel: MentionedChannel
}

const ContextMentionsList = ({
  mentions,
  hasMore,
  totalCount,
  visibleCount,
  panelId,
  onLoadMore,
  channel,
}: ContextMentionsListProps): JSX.Element => {
  return (
    <m.div
      key="context-mentions"
      id={panelId}
      data-testid="context-mentions-container"
      {...CONTEXT_MENTIONS_ANIMATION}
      layout="position"
      className={styles.contextMentionsContainer}
    >
      {hasMore && (
        <LoadMoreContextButton
          remainingCount={totalCount - visibleCount}
          increment={5}
          onClick={onLoadMore}
        />
      )}
      {mentions.map((contextMention) => (
        <m.div
          key={`context-${contextMention.id}`}
          {...MENTION_ITEM_ANIMATION}
          layout="position"
        >
          <MentionContextProvider mention={contextMention}>
            <MentionContextItem channel={channel} />
          </MentionContextProvider>
        </m.div>
      ))}
    </m.div>
  )
}

type MentionsContextSectionProps = {
  /**
   * The mention's ancestor thread, oldest first. Preloaded by the card's
   * on-expand conversation fetch, so toggling the section is instant.
   */
  context: MentionData[]
  /** True while the card's conversation fetch is still in flight. */
  loading: boolean
  /** The mentioned channel; its name is highlighted in each ancestor. */
  channel: MentionedChannel
}

/**
 * Mirror of `CommentsContextSection`. Renders inside the expanded
 * mention surface, above the main mention. The conversation root post is
 * not part of it; the card's top miniature surfaces that instead.
 *
 * Renders whenever the conversation has ancestors. Ancestors are
 * conversation-scoped rather than parent-scoped: any earlier mention on the
 * same post is context, including for a mention that hangs straight off the
 * post and so carries no `parentId`. Services whose webhooks omit a parent
 * comment ref (Instagram, LinkedIn) produce only that shape, so gating on
 * `parentId` would hide the thread from them entirely.
 *
 * The ancestor thread is preloaded by the card's single on-expand
 * conversation fetch (`context` prop), so toggling it is instant. The
 * section appears once that fetch resolves, matching the card's top
 * miniature; the spinner covers a later refetch.
 *
 * The Figma annotation calls for two affordances: show the thread inline
 * when we can, and a "view full thread" external link as the off-platform
 * fallback. `publicUrl` always resolves, so the link is always offered
 * alongside the inline thread.
 */
export const MentionsContextSection = React.forwardRef<
  HTMLDivElement,
  MentionsContextSectionProps
>(({ context, loading, channel }, ref) => {
  const mention = useMentionData()
  const ui = useContextMentions(mention.id, context)

  const externalLink = mention.publicUrl

  if (context.length === 0) {
    return null
  }

  const panelId = `context-panel-${mention.id}`

  return (
    <div ref={ref}>
      <ContextButtons
        showContext={ui.showContext}
        isLoading={loading}
        showSpinner={loading}
        externalLink={externalLink}
        panelId={panelId}
        onExpand={ui.handleToggleContext}
        onCollapse={ui.handleCollapseContext}
      />

      <AnimatePresence>
        {ui.showContextMentions && (
          <ContextMentionsList
            mentions={ui.visibleContextMentions}
            hasMore={ui.hasMore}
            totalCount={context.length}
            visibleCount={ui.visibleCount}
            panelId={panelId}
            onLoadMore={ui.handleShowMoreContext}
            channel={channel}
          />
        )}
      </AnimatePresence>
    </div>
  )
})

MentionsContextSection.displayName = 'MentionsContextSection'
